'use client'
import { GeneralInformation } from '@/payload-types'
import { useTranslations } from 'next-intl'
import { useHeaderTheme } from '@onclicks/sharedcomponents/HeaderTheme'
import { usePathname, useRouter } from '@onclicks/sharedmobilei18n/routing'
import React, { useEffect, useState } from 'react'
import { HeaderDesktop } from './HeaderDesktop/HeaderDesktop'
import { MobileNav } from './HeaderMobile/MobileNav'
import { cn } from '@/utilities/ui'
import {
  addressPath,
  ridesPath,
  contactsPath,
  driversPath,
  vehiclesPath,
  coursesPath,
  companiesPath,
  facturesPath,
  backofficeMessagesPath,
  clientMessagesPath,
  clientProfilePath,
  backofficeProfilePath,
  clientCompanyPath,
  backofficeCompanyPath,
} from '@/kmsApp/paths/paths'
import {
  CAN_ACCESS_ADDRESS,
  CAN_ACCESS_CLIENTS_ADMIN,
  CAN_ACCESS_COMPANIES_ADMIN,
  CAN_ACCESS_COMPANY_BACKOFFICE,
  CAN_ACCESS_COMPANY_CLIENT,
  CAN_ACCESS_COURSES_ADMIN,
  CAN_ACCESS_PROFILE_BACKOFFICE,
  CAN_ACCESS_PROFILE_CLIENT,
  CAN_ACCESS_RIDES,
  CAN_ACCESS_VEHICLES_ADMIN,
} from '@/kmsApp/constants/permissions-const'
import { roleUserVerification } from '@/kmsApp/utils/role-user-verification'
import { companyRedirectPath } from '@/kmsApp/utils/entreprise-permissions'
import { useUser } from '@/kmsApp/providers/user-provider'

export type NavLink = {
  label: string
  href: string
  permissions: string[]
  section?: 'main' | 'profile-actions'
  group?: string
  priority?: number
}

export const HeaderClient: React.FC<GeneralInformation> = (props) => {
  /* Storing the value in a useState to avoid hydration errors */
  const [theme, setTheme] = useState<string | null>(null)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()
  const router = useRouter()
  const { me } = useUser()
  const tAddress = useTranslations('favorite_addresses')
  const tHistory = useTranslations('history')
  const tEntreprise = useTranslations('web_entreprise')
  const tNav = useTranslations('nav')
  const tMenuPage = useTranslations('menu_page')
  const tProfile = useTranslations('profile')

  const links: NavLink[] = [
    {
      label: tAddress('title'),
      href: addressPath,
      permissions: [CAN_ACCESS_ADDRESS],
      section: 'main',
      group: 'address',
    },
    {
      label: tHistory('title'),
      href: ridesPath,
      permissions: [CAN_ACCESS_RIDES],
      section: 'main',
      group: 'rides',
    },
    {
      // Entre les deux, celui qui correspond a l'entreprise active gagne (cf.
      // bestByGroup ci-dessous) : un meme contact peut avoir a la fois le role
      // client et le role backoffice selon ses entreprises.
      label: tEntreprise('title'),
      href: clientCompanyPath,
      permissions: [CAN_ACCESS_COMPANY_CLIENT],
      section: 'main',
      group: 'company',
      priority: me?.entrepriseActive?.isPartenaire ? 1 : 10,
    },
    {
      label: tEntreprise('title'),
      href: backofficeCompanyPath,
      permissions: [CAN_ACCESS_COMPANY_BACKOFFICE],
      section: 'main',
      group: 'company',
      priority: me?.entrepriseActive?.isPartenaire ? 10 : 1,
    },
    {
      label: tNav('contacts'),
      href: contactsPath,
      permissions: [CAN_ACCESS_CLIENTS_ADMIN],
      section: 'main',
      group: 'clients',
    },
    {
      label: tNav('drivers'),
      href: driversPath,
      permissions: [CAN_ACCESS_COMPANIES_ADMIN],
      section: 'main',
      group: 'drivers',
    },
    {
      label: tNav('vehicles'),
      href: vehiclesPath,
      permissions: [CAN_ACCESS_VEHICLES_ADMIN],
      section: 'main',
      group: 'vehicles',
    },
    {
      label: tNav('courses'),
      href: coursesPath,
      permissions: [CAN_ACCESS_COURSES_ADMIN],
      section: 'main',
      group: 'courses',
    },
    {
      label: tNav('companies'),
      href: companiesPath,
      permissions: [CAN_ACCESS_COMPANIES_ADMIN],
      section: 'main',
      group: 'companies',
    },
    {
      label: tNav('factures'),
      href: facturesPath,
      permissions: [CAN_ACCESS_COMPANIES_ADMIN],
      section: 'main',
      group: 'factures',
    },
    {
      // La messagerie est ouverte a tout compte : c'est l'espace qui change, le
      // back-office ayant besoin de distinguer les deux fils d'une course.
      label: tNav('messages'),
      href: clientMessagesPath,
      permissions: [CAN_ACCESS_RIDES],
      section: 'main',
      group: 'messages',
      priority: 1,
    },
    {
      label: tNav('messages'),
      href: backofficeMessagesPath,
      permissions: [CAN_ACCESS_COURSES_ADMIN],
      section: 'main',
      group: 'messages',
      priority: 10,
    },
    {
      // Entre les deux, celui qui correspond a l'entreprise active gagne (cf.
      // bestByGroup ci-dessous) : un meme contact peut etre a la fois client
      // et partenaire selon l'entreprise selectionnee.
      label: tProfile('title'),
      href: backofficeProfilePath,
      permissions: [CAN_ACCESS_PROFILE_BACKOFFICE],
      section: 'profile-actions',
      group: 'profile',
      priority: me?.entrepriseActive?.isPartenaire ? 10 : 1,
    },
    {
      label: tProfile('title'),
      href: clientProfilePath,
      permissions: [CAN_ACCESS_PROFILE_CLIENT],
      section: 'profile-actions',
      group: 'profile',
      priority: me?.entrepriseActive?.isPartenaire ? 1 : 10,
    },
    {
      // Aucune permission requise : visible pour tout contact (simple passager
      // ou client-manager). La visibilite reelle est geree par le nombre
      // d'entreprises dans me.entreprisesDisponibles (cf. ConnectionMenu).
      label: tMenuPage('active_company'),
      href: clientCompanyPath,
      permissions: [],
      section: 'profile-actions',
      group: 'entreprise-active',
      priority: me?.entrepriseActive?.isPartenaire ? 1 : 10,
    },
    {
      // Idem cote backoffice : chauffeur simple ou driver-manager.
      label: tMenuPage('active_company'),
      href: backofficeCompanyPath,
      permissions: [],
      section: 'profile-actions',
      group: 'entreprise-active',
      priority: me?.entrepriseActive?.isPartenaire ? 10 : 1,
    },
  ]

  const accessible = links.filter((link) => roleUserVerification(me?.role, link.permissions))

  const bestByGroup = new Map<string, NavLink>()
  const ungrouped: NavLink[] = []

  for (const link of accessible) {
    if (!link.group) {
      ungrouped.push(link)
      continue
    }
    const current = bestByGroup.get(link.group)
    if (!current || (link.priority ?? 0) > (current.priority ?? 0)) {
      bestByGroup.set(link.group, link)
    }
  }

  const linksToDisplay = [...ungrouped, ...bestByGroup.values()]

  useEffect(() => {
    setHeaderTheme(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTheme])

  // vérifie s'il faut faire une redirection du au type de l'entreprise quand on change l'entrepriseActive
  // (pour passer de client/company à backoffice/company ou inversement)
  useEffect(() => {
    const redirectTo = companyRedirectPath(pathname, me?.entrepriseActive)
    if (redirectTo) router.replace(redirectTo)
  }, [me?.entrepriseActive, pathname, router])

  return (
    <header className={cn('relative z-999')}>
      <HeaderDesktop data={props} links={linksToDisplay} />
      <MobileNav data={props} links={linksToDisplay} />
    </header>
  )
}
